import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { UserImageWithProject } from './useUserImagesWithProjects';

interface UseInfiniteUserImagesOptions {
  searchQuery?: string;
  limit?: number;
}

interface PageData {
  data: UserImageWithProject[];
  totalCount: number;
  hasMore: boolean;
  nextPage?: number;
}

/**
 * Hook for infinite scrolling user images with search
 * Uses the new get_user_accessible_images Supabase function for optimized access control
 * 
 * UPDATED: Now uses server-side function that handles all access logic:
 * - Images owned by the user (private images)
 * - Images from workbooks where user has editor access
 * - Server-side search and pagination
 * - Proper total count and has_more flags
 */
export const useInfiniteUserImages = (options: UseInfiniteUserImagesOptions = {}) => {
  const { user } = useAuth();
  const { searchQuery = '', limit = 50 } = options;

  return useInfiniteQuery<PageData, Error, { pages: PageData[]; pageParams: number[] }, (string | number)[], number>({
    // Updated query key to reflect new data source
    queryKey: ['user-accessible-images', user?.id, searchQuery, limit],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<PageData> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const offset = pageParam * limit;

      // Use the new get_user_accessible_images function
      const { data: functionResult, error } = await supabase.rpc('get_user_accessible_images', {
        p_user_id: user.id,
        p_search_query: searchQuery.trim() || null, // Pass null if empty for better function handling
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        console.error('Error fetching user accessible images:', error);
        throw error;
      }

      if (!functionResult || functionResult.length === 0) {
        return {
          data: [],
          totalCount: 0,
          hasMore: false,
          nextPage: undefined
        };
      }

      // Extract metadata from first row (all rows have same metadata)
      const firstRow = functionResult[0];
      const totalCount = firstRow.total_count || 0;
      const hasMore = firstRow.has_more || false;

      // Get unique workbook IDs to fetch project titles
      const workbookIds = [...new Set(
        functionResult
          .filter(img => img.workbook_id)
          .map(img => img.workbook_id)
      )];
      
      let projects: Record<string, { id: string; title: string }> = {};
      if (workbookIds.length > 0) {
        // Fetch project titles for workbook context
        const { data: workbookData, error: workbookError } = await supabase
          .from('workbook')
          .select('id, title')
          .in('id', workbookIds);
          
        if (!workbookError && workbookData) {
          projects = workbookData.reduce((acc, wb) => {
            acc[wb.id] = { id: wb.id, title: wb.title };
            return acc;
          }, {} as Record<string, { id: string; title: string }>);
        }
      }

      // Transform function result to match UserImageWithProject interface
      const transformedData: UserImageWithProject[] = functionResult.map(item => ({
        id: item.id,
        image_url: item.image_url,
        filename: item.filename,
        user_id: item.user_id,
        workbook_id: item.workbook_id,
        created_at: item.created_at,
        generation_prompt: item.generation_prompt,
        width: item.width,
        height: item.height,
        // Add project information if available
        project: item.workbook_id && projects[item.workbook_id] ? projects[item.workbook_id] : null
      }));

      return {
        data: transformedData,
        totalCount,
        hasMore,
        nextPage: hasMore ? pageParam + 1 : undefined
      };
    },
    getNextPageParam: (lastPage: PageData) => lastPage.nextPage,
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}; 